import clsx from 'clsx';
import memoizeOne from 'memoize-one';
import * as React from 'react';

import { assert } from '@gitbook/util-ts';

import { BlockWidthWrapper } from './BlockWidthWrapper';

/**
 * Context to store the class name for the block layout.
 */
const ClassNameContext = React.createContext<string | undefined>(undefined);

/**
 * Set the class name for the block layout.
 */
export function BlockLayoutProvider(props: { children: React.ReactNode; className?: string }) {
    return (
        <ClassNameContext.Provider value={props.className}>
            {props.children}
        </ClassNameContext.Provider>
    );
}

/**
 * Defines how each block should render within the editor when given a layout. Each mode is a variation on
 * the size and margins of the blocks when rendered in a full-width editor.
 * - `centered` blocks are centered based on the editor layout, they will have margin and center themselves
 * based on a maximum width.
 * - `marginless-centered` blocks are centered blocks, but without any margins.
 * - `full-width` blocks are blocks with margin, but not centered in the editor.
 * - `unstyled` blocks render as-is and do not have any margins or centering applied to them.
 * - `content-width` blocks adjust their width to match the size of their content
 */
export type LayoutRenderModes =
    | 'centered'
    | 'marginless-centered'
    | 'full-width'
    | 'unstyled'
    | 'content-width';

export type BlockLayoutComponentProps = {
    /**
     * Defines the rendering mode for the blocks within that block layout component. The modes are explained in the
     * `LayoutRenderModes` types.
     */
    renderMode: LayoutRenderModes;
    children: React.ReactNode;
    onMouseEnter?: (event: React.MouseEvent) => void;
    onMouseLeave?: (event: React.MouseEvent) => void;
    className?: string;
};

const BlockLayoutMaxWidthContext = React.createContext<number | null>(null);

/**
 * Get the max width of the block layout based on the editor context.
 */
export function useBlockLayoutMaxWidth() {
    const size = React.useContext(BlockLayoutMaxWidthContext);
    assert(size !== null, 'BlockLayoutMaxWidthContext is not set');
    return size;
}

/**
 * Get the maximum width of the block layout based on the editor context.
 */
const getBlockLayoutMaxWidths = memoizeOne(function getBlockLayoutMaxWidths() {
    const computedStyle = window.getComputedStyle(document.body);
    const blockWrapperMaxWidth = computedStyle.getPropertyValue('--block-wrapper-max-width');
    const pageWrapperWidth = computedStyle.getPropertyValue('--page-layout-default-max-width');
    const pageWrapperPaddingX = computedStyle.getPropertyValue('--page-wrapper-padding-x');
    return {
        base: sizeToPx(blockWrapperMaxWidth),
        fullWidth: sizeToPx(pageWrapperWidth) - sizeToPx(pageWrapperPaddingX) * 2,
    };
});

/**
 * Convert a size string to a pixel value.
 */
function sizeToPx(size: string) {
    if (size.endsWith('px')) {
        return Number.parseInt(size.slice(0, -2), 10);
    }
    if (size.endsWith('rem')) {
        return Number.parseInt(size.slice(0, -3), 10) * 16;
    }
    throw new Error(`Invalid size: ${size}`);
}

/**
 * Render a responsive block based on the editor context.
 */
export function BlockLayout({ renderMode, ...props }: BlockLayoutComponentProps) {
    const className = React.useContext(ClassNameContext);
    const blockSizes = getBlockLayoutMaxWidths();

    if (['unstyled', 'full-width', 'content-width'].includes(renderMode)) {
        return (
            <BlockLayoutMaxWidthContext.Provider value={blockSizes.fullWidth}>
                <div
                    className={clsx(
                        'relative w-full',
                        renderMode === 'content-width' &&
                            'mx-auto page-block-openapi:ml-0 page-full-width:ml-0',
                        props.className
                    )}
                    data-render-mode={renderMode}
                    style={
                        {
                            '--block-max-width':
                                'calc(var(--page-layout-default-max-width) - var(--block-margin-x) * 2)',
                        } as React.CSSProperties
                    }
                    {...props}
                />
            </BlockLayoutMaxWidthContext.Provider>
        );
    }

    return (
        <BlockLayoutMaxWidthContext.Provider value={blockSizes.base}>
            <BlockWidthWrapper className={className} {...props} />
        </BlockLayoutMaxWidthContext.Provider>
    );
}
